// The floating toolbar over a text selection (#13194, artboard 3): Bold,
// Italic, Underline and Link (the formatting bar's own commands), then
// Comment when the host takes comments. Shown only for a non-empty text
// selection in an editable document; it hides on blur, on an empty selection
// and on Escape. Positioned from the editor's own coordinates
// (`view.coordsAtPos`) inside its scroll container, so it travels with the
// text as the document scrolls; no positioning library. It stays inside the
// editor's own area: a selection near the right edge shifts it left, so it is
// never under the comments column beside the editor or cut off by the pane
// (#13261, Hunter).
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import type { Editor } from "@tiptap/react";
import { TextSelection } from "@tiptap/pm/state";
import { Tooltip } from "@mui/material";
import {
  ChatCenteredText,
  Link as LinkIcon,
  TextAUnderline,
  TextB,
  TextItalic,
} from "@phosphor-icons/react";
import { normalizeLinkUrl } from "./services/linkUrl";
import type { CommentSelection } from "./services/documentCommentsBridge";

/** The toolbar's height and the gap it keeps above the selection. */
const TOOLBAR_HEIGHT_PX = 34;
const GAP_PX = 8;
/** The least it keeps from the editor's left and right edges. */
const EDGE_PX = 8;

interface Placement {
  top: number;
  left: number;
  from: number;
  to: number;
}

const SelectionToolbar: React.FC<{
  editor: Editor;
  /** The editor's scroll container; the toolbar is positioned inside it. */
  container: HTMLElement | null;
  /** Comment on the selection; absent when the host takes no comments. */
  onComment?: (selection: CommentSelection) => void;
}> = ({ editor, container, onComment }) => {
  const [placement, setPlacement] = useState<Placement | null>(null);
  const [linkDraft, setLinkDraft] = useState<string | null>(null);
  const toolbarRef = useRef<HTMLDivElement>(null);
  // The left the toolbar takes once measured: the selection's own left,
  // shifted so the whole toolbar fits inside the editor's visible width.
  // Measured before paint, so it never shows at the unclamped place.
  const [fittedLeft, setFittedLeft] = useState<number | null>(null);
  useLayoutEffect(() => {
    const toolbar = toolbarRef.current;
    if (!placement || !container || !toolbar) {
      setFittedLeft(null);
      return;
    }
    const min = container.scrollLeft + EDGE_PX;
    const max =
      container.scrollLeft +
      container.clientWidth -
      toolbar.offsetWidth -
      EDGE_PX;
    setFittedLeft(Math.max(min, Math.min(placement.left, max)));
    // The link field widens the toolbar: fitted again when it opens.
  }, [placement, container, linkDraft]);
  // The range Escape dismissed: the toolbar stays away until the selection
  // changes.
  const dismissed = useRef<{ from: number; to: number } | null>(null);

  const place = useCallback(() => {
    const { selection, doc } = editor.state;
    if (
      !editor.isEditable ||
      !editor.isFocused ||
      selection.empty ||
      !(selection instanceof TextSelection) ||
      doc.textBetween(selection.from, selection.to, " ").trim() === "" ||
      !container
    ) {
      setPlacement(null);
      return;
    }
    const { from, to } = selection;
    if (
      dismissed.current &&
      dismissed.current.from === from &&
      dismissed.current.to === to
    ) {
      setPlacement(null);
      return;
    }
    dismissed.current = null;
    const start = editor.view.coordsAtPos(from);
    const box = container.getBoundingClientRect();
    const above = start.top - box.top + container.scrollTop;
    const top =
      above - TOOLBAR_HEIGHT_PX - GAP_PX >= container.scrollTop
        ? above - TOOLBAR_HEIGHT_PX - GAP_PX
        : editor.view.coordsAtPos(to).bottom -
          box.top +
          container.scrollTop +
          GAP_PX;
    const left = Math.max(8, start.left - box.left + container.scrollLeft);
    setPlacement({ top, left, from, to });
  }, [editor, container]);

  useEffect(() => {
    const onSelection = () => {
      setLinkDraft(null);
      place();
    };
    const onBlur = ({ event }: { event: FocusEvent }) => {
      // Focus moving into the toolbar (the link field) keeps it.
      const next = event.relatedTarget as Node | null;
      if (next && toolbarRef.current?.contains(next)) return;
      setPlacement(null);
      setLinkDraft(null);
    };
    editor.on("selectionUpdate", onSelection);
    editor.on("focus", onSelection);
    editor.on("blur", onBlur);
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      const { from, to } = editor.state.selection;
      dismissed.current = { from, to };
      setPlacement(null);
      setLinkDraft(null);
    };
    // On the scroll container, which holds the editor: Escape in the text
    // bubbles to it.
    container?.addEventListener("keydown", onKeyDown);
    return () => {
      editor.off("selectionUpdate", onSelection);
      editor.off("focus", onSelection);
      editor.off("blur", onBlur);
      container?.removeEventListener("keydown", onKeyDown);
    };
  }, [editor, container, place]);

  if (!placement) return null;

  // Every button keeps the editor's focus and selection: a mousedown that
  // moved focus would blur the editor and take the toolbar down first.
  const keepSelection = (event: React.MouseEvent) => event.preventDefault();

  const applyLink = () => {
    const url = linkDraft?.trim() ?? "";
    const chain = editor.chain().focus().extendMarkRange("link");
    if (url === "") chain.unsetLink().run();
    else chain.setLink({ href: normalizeLinkUrl(url) }).run();
    setLinkDraft(null);
  };

  const formatButton = (
    label: string,
    active: boolean,
    onClick: () => void,
    icon: React.ReactNode
  ) => (
    <Tooltip title={label} arrow placement="top" describeChild>
      <button
        type="button"
        aria-label={label}
        aria-pressed={active}
        onMouseDown={keepSelection}
        onClick={onClick}
        className={`flex h-7 w-7 cursor-pointer items-center justify-center rounded-[6px] text-[var(--fp-ink)] hover:bg-[var(--fp-hover)] ${
          active ? "bg-[var(--fp-hover)]" : ""
        }`}
      >
        {icon}
      </button>
    </Tooltip>
  );

  return (
    <div
      ref={toolbarRef}
      role="toolbar"
      aria-label="Selection"
      style={{ top: placement.top, left: fittedLeft ?? placement.left }}
      className="absolute z-20 flex h-[34px] items-center gap-0.5 rounded-[9px] border border-[var(--fp-border)] bg-[var(--fp-surface)] px-[5px] [box-shadow:var(--fp-lift)]"
    >
      {linkDraft !== null ? (
        <input
          aria-label="Link address"
          value={linkDraft}
          autoFocus
          placeholder="Paste a link"
          onChange={(e) => setLinkDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              applyLink();
            } else if (e.key === "Escape") {
              e.preventDefault();
              setLinkDraft(null);
              editor.commands.focus();
            }
          }}
          onBlur={() => setLinkDraft(null)}
          className="h-7 w-[220px] rounded-[6px] bg-transparent px-2 text-[13px] text-[var(--fp-ink)] outline-none"
        />
      ) : (
        <>
          {formatButton(
            "Bold",
            editor.isActive("bold"),
            () => editor.chain().focus().toggleBold().run(),
            <TextB size={16} />
          )}
          {formatButton(
            "Italic",
            editor.isActive("italic"),
            () => editor.chain().focus().toggleItalic().run(),
            <TextItalic size={16} />
          )}
          {formatButton(
            "Underline",
            editor.isActive("underline"),
            () => editor.chain().focus().toggleUnderline().run(),
            <TextAUnderline size={16} />
          )}
          {formatButton(
            "Link",
            editor.isActive("link"),
            () =>
              setLinkDraft(
                (editor.getAttributes("link").href as string | undefined) ?? ""
              ),
            <LinkIcon size={16} />
          )}
          {onComment && (
            <>
              <span
                aria-hidden="true"
                className="mx-1 h-4 w-px bg-[var(--fp-border-strong)]"
              />
              <button
                type="button"
                onMouseDown={keepSelection}
                onClick={() => {
                  const { from, to } = placement;
                  setPlacement(null);
                  onComment({
                    quote: editor.state.doc.textBetween(from, to, " "),
                    from,
                    to,
                  });
                }}
                className="flex h-7 cursor-pointer items-center gap-1.5 rounded-[7px] bg-[#F7F8FF] pl-2 pr-2.5 text-[13px] font-medium text-[#2A3DCB] hover:bg-[#EEF1FF]"
              >
                <ChatCenteredText size={15} aria-hidden />
                Comment
              </button>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default SelectionToolbar;
